<template>
    <div class="px-4 full-height">
        <div class="d-flex align-items-center justify-content-between my-4">
            <div class="d-flex align-items-center col-sm-3 pl-0">
                <div class="d-flex w-100">
                    <input class="glued-right bg-lighter w-100" type="text" v-model="searchQuery" placeholder="Search">
                    <div class="glued-left dark pointer">
                        <i class="ico-search larger color-1 search-icon-align"></i>
                    </div>
                </div>
            </div>

            <button class="btn" @click="addRulesGroup" v-if="account.accessType === 1">
                <i class="ico ico-add mt-1"></i> Add Rules Group
            </button>
        </div>

        <div class="d-flex flex-column justify-content-center align-items-center vh-100" v-if="getLoadingGroups">
            <span class="dark bold loading">Loading</span>
            <loader></loader>
        </div>
        <div v-else>
            <list :header="header" :body="body" :rows="filteredResults" :actions="actions"></list>
        </div>
    </div>
</template>

<script>
    import { mapGetters, mapActions } from 'vuex'
    import { rulesMixin } from '@/mixins/rules'

    import List from '@/tools/list/baseList'
    import Loader from '@/tools/loaders/loader1'

    export default {
        data() {
            return {
                searchQuery: null,
                showInactive: false,
                header: {
                    selection: {
                        active: false,
                    },
                    list: {
                        active: 'Active',
                        name: 'Name',
                        bookingWindow: 'Booking Window',
                        occupation: 'Occupation',
                        notification: 'Notification',
                    },
                    sort: {
                        list: [{ key: 'name', direction: 'asc' }],
                    }
                },
                body: {
                    selection: {
                        active: false,
                    },
                    customCells: {
                        active: this.formatActive,
                        bookingWindow: this.formatBookingWindow,
                        occupation: this.formatOccupation,
                        notification: this.formatNotification,
                    },
                    customClassCell: {
                        active: 'rules-active-row-size'
                    },
                    customRowClass: this.isValidGroup,
                    customRowEvent: this.edit,
                },
                actions: {
                    label : 'Actions',
                    list: [
                        {
                            slug: 'duplicate',
                            title: 'Duplicate',
                            tooltip: 'Duplicate',
                            params: '',
                            class: '',
                            icon: 'fas fa-clone largest',
                            event: this.duplicateRule,
                        },
                        {
                            slug: 'edit',
                            title: 'Edit',
                            tooltip: 'Edit',
                            params: '',
                            class: '',
                            icon: 'ico-edit largest',
                            event: this.edit,
                        },
                        {
                            slug: 'delete',
                            title: 'Delete',
                            tooltip: 'Delete',
                            params: '',
                            class: '',
                            icon: 'ico-delete largest',
                            event: this.deleteRuleGroups,
                        }
                    ],
                }
            }
        },
        mixins: [
            rulesMixin,
        ],
        computed: {
            ...mapGetters([
               'getRulesGroups',
               'getLoadingGroups',
               'account'
            ]),
            filteredResults() {
                if (this.searchQuery) {
                    return this.getRulesGroups.filter(category => {
                        return category.name.toLowerCase().includes(this.searchQuery.toLowerCase());
                    })
                } else {
                    return this.getRulesGroups;
                }
            }
        },
        methods:{
            ...mapActions([
                'deleteRuleGroup',
                'setSelectedRuleItem',
                'openModal',
                'downloadRulesGroup',
                'downloadRules',
                'startFullScreenLoading',
                'stopFullScreenLoading'
            ]),
            toggleShowInactive(e) {
                this.showInactive = e.value;
            },
            addRulesGroup() {
                this.$router.push({ name: 'pluralo.rulesGroup.new' });
            },
            duplicateRule(item) {
                this.setSelectedRuleItem({type: 'rulesGroups', id: item.id})
                this.openModal('RulesDuplicate')
            },
            edit(item) {
                if (item.id !== undefined) {
                    this.setSelectedRuleItem({ type: 'rulesGroups', id: item.id });
                    this.$router.push({ name: 'pluralo.rules.editRuleGroup', params: { id: item.id } });
                }
            },
            deleteRuleGroups(ruleGroup) {
                const finalRuleGroup = { ...ruleGroup, isDeleted: true };

                this.$dialog.confirm(`
                    <div class="d-flex flex-column justify-content-center">
                        <div class="fs-largest font-weight-600 mb-4">You are about to delete ${ruleGroup.hasProductGroupAssossiation ? 'an <span class="color-6"> Active</span> Rule Group' : 'a Rule Group'}!</div>
                        <div class="dark mb-4">This action is irreversible and will permanently delete the Rule Group.</div>
                        <div class="dark">Are you sure you want to proceed?</div>
                    </div>`,
                    { okText: 'Yes', cancelText: 'No' }
                ).then(() => {
                    this.$toasted.info('Deleting', { duration: 2000 });
                    this.startFullScreenLoading();   

                    finalRuleGroup.priceRules.map((groupRule) => {
                        groupRule.priceRuleId = groupRule.priceRule.id;
                        delete groupRule.priceRule;
                    })
                    
                    this.deleteRuleGroup(finalRuleGroup).then(success => {
                        if (success) {
                            this.$toasted.success('Successfully deleted', { duration: 2000 });
                        } else {
                            this.$toasted.error('An error occurred', { duration: 2000 });
                        }
                        
                        this.downloadRules();
                        this.downloadRulesGroup();
                        this.stopFullScreenLoading();
                    }).catch(error => {
                        this.$toasted.error('An unexpected error occurred', { duration: 2000 });
                        console.error(error);
                        this.stopFullScreenLoading();
                    })
                });
            },
            formatActive(item) {
                return `<div class="rules-active-circle ${item.hasProductGroupAssossiation ? 'active' : 'inactive'}"></div>`;
            },
            isValidGroup(item) {
                return !item.isValid ? 'invalid-group-warning' : '';
            },
            formatBookingWindow(item) {
                const bookingWindowTypes = ['No Restrictions', 'Days', 'Hours', 'Event Start'];
                const bwFromType = bookingWindowTypes[item.bookingWindowFromType] || 'Invalid';
                
                const bookingWindowIndexToType = item.bookingWindowToType === 0 ? 3 : item.bookingWindowToType;
                const bwToType = bookingWindowTypes[bookingWindowIndexToType] || 'Invalid';

                const formatTime = (time) => {
                    const [hours, minutes] = time.split(':');
                    return `${hours}:${minutes}`;
                };

                const bwFromVal = item.bookingWindowFromType === 2 ? formatTime(item.bookingWindowFrom) : item.bookingWindowFrom;
                const bwToVal = item.bookingWindowToType === 2 ? formatTime(item.bookingWindowTo) : item.bookingWindowTo;

                const fromText = item.bookingWindowFromType === 0 ? bwFromType : `${bwFromVal} ${bwFromType}`;
                const toText = item.bookingWindowToType === 0 ? bwToType : `${bwToVal} ${bwToType}`;

                return  `<div>
                            ${item.isBookingWindowActive ? 'ON' : 'OFF'} 
                            ${item.isBookingWindowActive ? `| <span class="font-weight-700">From: </span>${fromText} - <span class="bold font-weight-700">Until: </span>${toText}` : ''}
                        </div>`;
            },
            formatOccupation(item) {
                const occupationTypeSymbols = {1: '>', 2: '≥', 3: '<', 4: '≤'};
                const occupationTypeSymbol = occupationTypeSymbols[item.occupancyType] || '';

                return item.isBookingWindowActive ? `<div><span class="font-weight-700">${occupationTypeSymbol}</span> ${item.occupancy}%</div>` 
                                                  : `<div><span class="font-weight-700">-</span></div>`;
            },
            formatNotification(item) {
                return `<div>${item.sendNotificationWhenRuleApplied ? 'ACTIVE' : 'INACTIVE'}</div>`;
            }
        },
        components:{
            Loader,
            List
        }
    }
</script>

<style scoped lang="scss">
    .full-height {
        min-height: 77vh;
    }

    .search-icon-align {
        position: relative;
        left: 4px;
        top: 1px;
    }
    
    .pointer {
        cursor: pointer;
    }
</style>

<style lang="scss">
    .rules-active-circle {
        height: 12px;
        width: 12px;
        display: block;
        border-radius: 52%;
        margin-left: 12px;

        &.active {
             background-color: #6ac951;
        }

        &.inactive {
            background-color: #d4d4d4;
            
        }
    }

    .rules-active-row-size {
        width: 80px;
    }

    .invalid-group-warning {
        background-color: #ffe5e5;
    }
</style>



// WEBPACK FOOTER //
// TabRulesGroup.vue?54746a08